import {FormEvent, useId, useState} from "react";
import {Badge, Button, Form, Modal, Table} from "react-bootstrap";
import {Link, useNavigate} from "react-router";
import {ProductsApi} from "../../api/ProductsApi";
import {useAsync} from "../../components/useAsync";
import {ErrorAlert, fieldError} from "../../components/ErrorAlert";
import {LoadingSpinner} from "../../components/LoadingSpinner";
import {PageHeader} from "../../components/PageHeader";
import {SubmitButton} from "../../components/SubmitButton";
import {formatDate} from "../../components/format";
import {SortHeader, useSort} from "../../components/sortable";
import {Field, FieldControl} from "../../components/Field";

// The product catalog, published or not.
export function ProductsPage() {
    const navigate = useNavigate();
    const {data: products, error, loading} = useAsync(() => ProductsApi.getAll(), []);
    const [creating, setCreating] = useState(false);
    const {sorted, sort, toggle} = useSort('admin-products:sort', products, {
        name: x => x.name,
        sku: x => x.sku,
        keyPrefix: x => x.keyPrefix,
        version: x => x.currentVersion?.version,
        modified: x => x.modifiedOn
    });

    return (
        <>
            <PageHeader title="Products">
                <Button onClick={() => setCreating(true)}><i className="bi bi-plus-lg me-1"></i>New product</Button>
            </PageHeader>
            <ErrorAlert error={error}/>
            {loading && !products && <LoadingSpinner/>}
            {products && (
                <Table responsive hover>
                    <thead>
                    <tr>
                        <SortHeader column="name" sort={sort} onSort={toggle}>Name</SortHeader>
                        <SortHeader column="sku" sort={sort} onSort={toggle}>SKU</SortHeader>
                        <SortHeader column="keyPrefix" sort={sort} onSort={toggle}>Key prefix</SortHeader>
                        <SortHeader column="version" sort={sort} onSort={toggle}>Current version</SortHeader>
                        <SortHeader column="modified" sort={sort} onSort={toggle}>Modified</SortHeader>
                        <th></th>
                    </tr>
                    </thead>
                    <tbody>
                    {sorted.map(product => (
                        <tr key={product.id} className="clickable-row" onClick={() => navigate(`/admin/products/${product.id}`)}>
                            <td>{product.name}</td>
                            <td className="license-key">{product.sku}</td>
                            <td className="license-key">{product.keyPrefix}</td>
                            <td>
                                {product.currentVersion?.version ?? <span className="text-body-secondary">None</span>}
                                {product.currentEarlyAccessVersion && <span className="d-block small text-body-secondary">Early Access {product.currentEarlyAccessVersion.version}</span>}
                            </td>
                            <td>{formatDate(product.modifiedOn)}</td>
                            <td className="text-end">
                                {!product.published && <Badge bg="secondary" className="me-2">Unpublished</Badge>}
                                {product.beta && <Badge bg="info" className="me-2">Early Access</Badge>}
                                <Link to={`/admin/products/${product.id}`} onClick={e => e.stopPropagation()}>Open</Link>
                            </td>
                        </tr>
                    ))}
                    {products.length === 0 && <tr><td colSpan={6} className="text-center text-body-secondary py-4">No products.</td></tr>}
                    </tbody>
                </Table>
            )}
            <CreateProductModal show={creating} onHide={() => setCreating(false)} onCreated={id => navigate(`/admin/products/${id}`)}/>
        </>
    );
}

function CreateProductModal({show, onHide, onCreated}: { show: boolean, onHide(): void, onCreated(id: string): void }) {
    const titleId = useId();
    const [name, setName] = useState('');
    const [sku, setSku] = useState('');
    const [keyPrefix, setKeyPrefix] = useState('');
    const [validated, setValidated] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<unknown>();

    const prefixValid = /^[A-Za-z0-9]{2,8}$/.test(keyPrefix.trim());

    const submit = async (e: FormEvent) => {
        e.preventDefault();
        setValidated(true);
        if (!name.trim() || !sku.trim() || !prefixValid) {
            return;
        }
        setBusy(true);
        setError(undefined);
        try {
            const created = await ProductsApi.create({
                name: name.trim(),
                description: '',
                sku: sku.trim(),
                keyPrefix: keyPrefix.trim(),
                prices: [],
                published: false,
                beta: false
            });
            onCreated(created.id);
        } catch (err) {
            setError(err);
        } finally {
            setBusy(false);
        }
    };

    return (
        <Modal aria-labelledby={titleId} show={show} onHide={onHide} centered>
            <Form noValidate onSubmit={submit}>
                <Modal.Header closeButton><Modal.Title id={titleId}>New product</Modal.Title></Modal.Header>
                <Modal.Body>
                    <ErrorAlert error={error} shownFields={['name', 'sku', 'keyPrefix']}/>
                    <Field className="mb-3" controlId="new-product-name" label="Name" error={(validated && !name.trim() && 'Enter a name.') || fieldError(error, 'name')}>
                        <FieldControl value={name} onChange={e => setName(e.target.value)}/>
                    </Field>
                    <Field className="mb-3" controlId="new-product-sku" label="SKU" error={(validated && !sku.trim() && 'Enter a SKU.') || fieldError(error, 'sku')}
                           help="The product sends this to the license server and the update check. Stored in upper case.">
                        <FieldControl className="license-key" value={sku} onChange={e => setSku(e.target.value)}/>
                    </Field>
                    <Field controlId="new-product-prefix" label="License key prefix" error={(validated && !prefixValid && '2 to 8 letters or digits.') || fieldError(error, 'keyPrefix')}
                           help="The first group of generated license keys.">
                        <FieldControl className="license-key" placeholder="ASTREL" value={keyPrefix} onChange={e => setKeyPrefix(e.target.value)}/>
                    </Field>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={onHide} disabled={busy}>Cancel</Button>
                    <SubmitButton busy={busy}>Create product</SubmitButton>
                </Modal.Footer>
            </Form>
        </Modal>
    );
}
